// ============================================================
// Damsel in Defense — auto-attach $10/month "Monthly Access Fee",
// quantity MIRRORED with the D-SOL kit(s).
//
// Multi-SKU safe: any number of kit variant IDs, in any combination,
// including several different kit variants in the cart at once.
//
// Craft/Dawn-safe: hooks the theme's own updateQuantity; NO cartUpdate
// publishing and NO global MutationObserver (those broke the steppers).
// Late-rendered product forms are picked up lazily on interaction instead.
//
// Checkout is gated: every Checkout click reconciles the fee before the
// browser leaves the theme, because nothing of ours runs on /checkout.
// "Buy it now" is hidden on kit variants — it bypasses the cart entirely.
//
// Capped at ONE kit and ONE fee, in total, across every kit variant. The
// quantity steppers are hidden on those lines, but hiding is only the
// affordance — the cap is enforced against /cart.js on every path.
//
// The kit and the fee are one purchase, so removal is symmetric: deleting
// either one deletes the other.
// ============================================================
(function () {
  'use strict';

  var KIT_VARIANT_IDS     = [44873305686105]; // D-SOL kit variant(s)
  var FEE_VARIANT_ID      = 42894152269913;   // Monthly Access Fee variant
  var FEE_SELLING_PLAN_ID = 1927020633;       // Recharge $10/month plan

  var MAX_KITS = 1;      // kit units allowed in the cart, summed across variants

  // Dawn's drawer renders <cart-drawer-items>, which extends CartItems but
  // does NOT match a bare 'cart-items' selector — the old code missed it.
  var CART_ITEMS_SEL = 'cart-items, cart-drawer-items';

  var SYNC_MS    = 700;  // how long our own writes are ignored
  var SETTLE_MS  = 500;  // wait for the theme's own change.js to land

  var DEBUG = false;      // set false once the fee attaches reliably

  function log() {
    if (!DEBUG || !window.console) return;
    console.log.apply(console, ['[MAF]'].concat([].slice.call(arguments)));
  }
  function warn() {
    if (!window.console) return;
    console.warn.apply(console, ['[MAF]'].concat([].slice.call(arguments)));
  }

  // ---- Re-entrancy guard --------------------------------------------------
  // Module-level, NOT an element property: the theme replaces the <cart-items>
  // node when it re-renders sections, which silently dropped the old flag.
  var syncing    = false;
  var syncTimer  = null;
  var lastFeeQty = null;  // fallback for themes that don't pass variantId
  // Last cart we fetched. Lets the quantity-hiding pass map a row's data-index
  // to a variant without firing a /cart.js request on every pointerdown.
  var lastCart   = null;

  function hold() {
    syncing = true;
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () { syncing = false; }, SYNC_MS);
  }

  // ---- Cart helpers -------------------------------------------------------
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { lastCart = cart; return cart; });
  }
  // The live cart renderer, drawer included. Always re-queried: the theme
  // replaces this node wholesale when it re-renders sections.
  function cartItemsEl() {
    return document.querySelector(CART_ITEMS_SEL);
  }
  function isKitVariant(id) {
    return KIT_VARIANT_IDS.indexOf(id) !== -1;
  }
  function kitLines(cart) {
    return cart.items.filter(function (i) { return isKitVariant(i.variant_id); });
  }
  function kitCount(cart) {
    return kitLines(cart).reduce(function (n, i) { return n + i.quantity; }, 0);
  }
  function feeLine(cart) {
    return cart.items.find(function (i) { return i.variant_id === FEE_VARIANT_ID; });
  }
  function feeHasPlan(l) {
    return l && l.selling_plan_allocation &&
           l.selling_plan_allocation.selling_plan &&
           l.selling_plan_allocation.selling_plan.id === FEE_SELLING_PLAN_ID;
  }
  function lineNumberOf(cart, item) {
    return cart.items.indexOf(item) + 1; // Shopify line numbers are 1-based
  }
  // Every cart write goes through here. The old code used bare fetch(), which
  // resolves successfully on a 422 — so a rejected add (bad selling plan,
  // unavailable variant, subscription rules) failed completely silently.
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        if (!r.ok) {
          warn(url + ' -> ' + r.status, data.description || data.message || data, 'sent:', body);
          throw new Error(url + ' ' + r.status);
        }
        log(url + ' ok', body);
        return data;
      });
    });
  }
  function addFee(qty) {
    return postJSON('/cart/add.js', {
      items: [{ id: FEE_VARIANT_ID, quantity: qty, selling_plan: FEE_SELLING_PLAN_ID }]
    });
  }
  function changeByKey(key, qty) {
    return postJSON('/cart/change.js', { id: key, quantity: qty });
  }
  // Key-based multi-line write. Atomic, and immune to the line-index shifting
  // that happens the moment one line drops to 0 — which is exactly what broke
  // sequential change.js calls across several kit lines.
  function updateByKeys(updates) {
    return postJSON('/cart/update.js', { updates: updates });
  }

  // ---- Spreading a fee-side edit across every kit line --------------------
  // Increase: the first kit line absorbs it.
  // Decrease: drained from the last kit line backwards, so the line the
  // customer most likely thinks of as "the" kit is the last one to shrink.
  function planKitQuantities(cart, target) {
    var lines   = kitLines(cart);
    var total   = kitCount(cart);
    var updates = {};
    var delta   = target - total;

    if (!lines.length || delta === 0) return updates;

    if (delta > 0) {
      updates[lines[0].key] = lines[0].quantity + delta;
      return updates;
    }

    var remove = -delta;
    for (var n = lines.length - 1; n >= 0 && remove > 0; n--) {
      var take = Math.min(lines[n].quantity, remove);
      updates[lines[n].key] = lines[n].quantity - take;
      remove -= take;
    }
    return updates;
  }

  // ---- Hiding the quantity steppers on capped lines -----------------------
  // Cosmetic only. The cap is enforced against /cart.js in reconcile() and
  // ensureFee(); this just removes the affordance so nobody tries.
  //
  // Dawn keeps <cart-remove-button> OUTSIDE <quantity-input>, so hiding the
  // stepper alone leaves the remove link intact — which is the whole way a
  // customer gets rid of the kit/fee pair.
  var STYLE_ID = 'maf-style';
  var HIDE_CLS = 'maf-qty-hidden';

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    // Dawn 11+ stamps data-quantity-variant-id on each cart quantity input,
    // so a static :has() rule hides the stepper before it can ever paint.
    // Kept as its own rule: on a browser without :has() it is dropped on its
    // own and the class-based fallback below still applies.
    var hasRule = KIT_VARIANT_IDS.concat([FEE_VARIANT_ID]).map(function (id) {
      return 'quantity-input:has(input[data-quantity-variant-id="' + id + '"])';
    }).join(',');

    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = hasRule + '{display:none !important;}' +
                    '.' + HIDE_CLS + '{display:none !important;}';
    (document.head || document.documentElement).appendChild(s);
  }

  function setHidden(node, hidden) {
    if (!node || !node.classList) return;
    if (hidden) node.classList.add(HIDE_CLS);
    else node.classList.remove(HIDE_CLS);
  }

  // Dawn renders the product quantity selector as a SIBLING of <product-form>
  // and wires it up with the form="" attribute, so it is not inside pf.
  // form.elements includes form-associated controls that live outside the
  // <form>, which is the only reliable way to reach it.
  function productQuantityInput(pf) {
    var form = pf.querySelector('form');
    var el   = (form && form.elements) ? form.elements.quantity : null;
    if (el) return el.length ? el[0] : el;
    return pf.querySelector('[name="quantity"]');
  }

  function lockProductQuantities() {
    document.querySelectorAll('product-form').forEach(function (pf) {
      var idInput = pf.querySelector('[name="id"]');
      var vid     = idInput ? parseInt(idInput.value, 10) : NaN;
      var q       = productQuantityInput(pf);
      if (!q || !q.closest) return;

      var wrap = q.closest('.product-form__quantity') || q.closest('quantity-input');
      if (!wrap) return;

      var isKit = isKitVariant(vid);
      setHidden(wrap, isKit);
      // Re-evaluated on every variant change, so a customer who set 3 on a
      // normal variant and then switched to the kit still submits 1.
      if (isKit) { q.value = String(MAX_KITS); q.setAttribute('max', String(MAX_KITS)); }
    });
  }

  function lockCartQuantities() {
    document.querySelectorAll(CART_ITEMS_SEL).forEach(function (root) {
      root.querySelectorAll('input[data-index]').forEach(function (input) {
        var stamped = input.getAttribute('data-quantity-variant-id');
        var vid;
        if (stamped) {
          vid = parseInt(stamped, 10);
        } else {
          // Older Dawn/Craft: fall back to the 1-based line index against the
          // cart we last fetched. Skipped rather than guessed if we have none.
          var idx  = parseInt(input.getAttribute('data-index'), 10);
          var item = (lastCart && idx > 0) ? lastCart.items[idx - 1] : null;
          if (!item) return;
          vid = item.variant_id;
        }
        var wrap = input.closest ? input.closest('quantity-input') : null;
        if (!wrap) return;
        setHidden(wrap, isKitVariant(vid) || vid === FEE_VARIANT_ID);
      });
    });
  }

  function lockQuantities() {
    injectStyle();
    lockProductQuantities();
    lockCartQuantities();
  }

  // ---- Product-page: seed the fee at the right quantity -------------------
  function hookProductForms() {
    document.querySelectorAll('product-form').forEach(function (pf) {
      if (pf.__mafHooked || typeof pf.onSubmitHandler !== 'function') return;
      pf.__mafHooked = true;

      var orig = pf.onSubmitHandler.bind(pf);
      pf.onSubmitHandler = function (evt) {
        var idInput = pf.querySelector('[name="id"]');
        var vid     = idInput ? parseInt(idInput.value, 10) : NaN;
        if (!isKitVariant(vid)) return orig(evt);

        if (evt && evt.preventDefault) evt.preventDefault();

        // Whatever the (hidden) stepper holds, the theme submits one.
        var q = productQuantityInput(pf);
        if (q) q.value = String(MAX_KITS);

        return getCart().then(function (cart) {
          // One kit in total, so clear EVERY existing kit line first —
          // including this variant's own. orig(evt) then adds a single unit,
          // which makes picking the other kit variant a swap rather than a
          // stack, and stops add.js merging a second unit onto an existing line.
          var clear = {};
          kitLines(cart).forEach(function (l) { clear[l.key] = 0; });

          return (Object.keys(clear).length ? updateByKeys(clear) : Promise.resolve())
            .then(getCart)
            .then(function (fresh) {
              var l = feeLine(fresh);
              if (!l) return addFee(MAX_KITS);
              if (!feeHasPlan(l)) {
                return changeByKey(l.key, 0).then(function () { return addFee(MAX_KITS); });
              }
              if (l.quantity !== MAX_KITS) return changeByKey(l.key, MAX_KITS);
            });
        }).catch(function () {}).then(function () { return orig(evt); });
      };
    });
  }

  // ---- Cart-page: mirror quantities via the theme's own updateQuantity ----
  // CartDrawerItems extends CartItems and inherits updateQuantity rather than
  // redefining it. Wrapping each element's immediate prototype would install
  // the wrapper twice on the chain and double every reconcile, so walk up to
  // the prototype that actually OWNS the method and wrap that one — which
  // covers the cart page and the drawer with a single patch.
  function updateQuantityOwner(el) {
    var p = Object.getPrototypeOf(el);
    while (p && !Object.prototype.hasOwnProperty.call(p, 'updateQuantity')) {
      p = Object.getPrototypeOf(p);
    }
    return p;
  }

  function hookCartItems() {
    document.querySelectorAll(CART_ITEMS_SEL).forEach(function (ci) {
      var proto = updateQuantityOwner(ci);
      if (!proto || proto.__mafWrapped || typeof proto.updateQuantity !== 'function') return;
      proto.__mafWrapped = true;

      var origUQ = proto.updateQuantity;
      proto.updateQuantity = function (line, quantity, event, name, variantId) {
        var result = origUQ.call(this, line, quantity, event, name, variantId);
        // The theme has just replaced the rows; re-hide the capped steppers
        // even when the write was our own.
        setTimeout(lockQuantities, SETTLE_MS);
        if (syncing) return result; // this call is our own mirroring write
        setTimeout(function () { reconcile(event, variantId); }, SETTLE_MS);
        return result;
      };
    });
  }

  // Drive the whole cart to `target` kit units AND a fee line of the same
  // quantity, in that order. Used by every cart-page correction, so the
  // clamp and the mirror are the same code path.
  function normalize(cart, target, event) {
    var updates = planKitQuantities(cart, target);

    var step = Object.keys(updates).length
      ? updateByKeys(updates).then(getCart)
      : Promise.resolve(cart);

    return step.then(function (fresh) {
      var el = cartItemsEl();
      var f  = feeLine(fresh);
      lastFeeQty = target;
      if (!el || !f) return;
      hold();
      // Re-setting the fee to the value it already holds is a no-op write
      // that still makes the theme re-render its sections — this is how we
      // repaint after a key-based update without publishing cartUpdate.
      el.updateQuantity(lineNumberOf(fresh, f), target, event, 'maf', FEE_VARIANT_ID);
    });
  }

  // Empty the cart of kits after the customer removed the fee.
  // Every kit line but the last goes in one atomic key-based write; the final
  // removal is handed to the theme so it renders the result itself — which is
  // what draws the empty-cart template without a reload.
  function clearAllKits(cart, event) {
    var lines = kitLines(cart);
    if (!lines.length) return Promise.resolve();

    var rest = {};
    for (var n = 0; n < lines.length - 1; n++) rest[lines[n].key] = 0;

    var step = Object.keys(rest).length
      ? updateByKeys(rest).then(getCart)
      : Promise.resolve(cart);

    return step.then(function (fresh) {
      var last = kitLines(fresh)[0];
      if (!last) return;
      var el = cartItemsEl();
      if (!el || typeof el.updateQuantity !== 'function') {
        // Nothing to drive the render with — write it and reload instead.
        return changeByKey(last.key, 0).then(function () { window.location.reload(); });
      }
      hold();
      el.updateQuantity(lineNumberOf(fresh, last), 0, event, 'maf', last.variant_id);
    });
  }

  function reconcile(event, variantId) {
    return getCart().then(function (cart) {
      var live = cartItemsEl(); // always the current node
      if (!live) return;

      var kits = kitCount(cart);
      var fee  = feeLine(cart);

      // No kits left -> drop the fee.
      if (kits === 0) {
        lastFeeQty = 0;
        if (fee) {
          hold();
          live.updateQuantity(lineNumberOf(cart, fee), 0, event, 'maf', FEE_VARIANT_ID);
        }
        return;
      }

      if (!fee) {
        // We knew a fee was on this cart a moment ago and now it is gone, so
        // the customer just removed it. Kit and fee are one purchase: the kit
        // goes with it. This mirrors the kits === 0 branch above.
        if (lastFeeQty !== null && lastFeeQty > 0) {
          lastFeeQty = 0;
          return clearAllKits(cart, event);
        }
        // We never saw a fee here — genuine drift, not a removal. Restore it.
        // (Only reachable from a cart-page edit; page-load repair is
        // ensureFee's job and always adds rather than deletes.)
        lastFeeQty = null;
        return addFee(Math.min(kits, MAX_KITS));
      }

      // Which side did the customer actually edit? Prefer the variantId the
      // theme hands us; fall back to comparing the fee against its last value.
      var editedFee = (variantId !== undefined && variantId !== null)
        ? parseInt(variantId, 10) === FEE_VARIANT_ID
        : (lastFeeQty !== null && fee.quantity !== lastFeeQty);

      // The edited side sets the intent; the cap trims it. Anything above the
      // cap that reached the cart another way (stale DOM, a direct
      // /cart/change.js, an app write) is pulled back here too.
      var target = Math.min(editedFee ? fee.quantity : kits, MAX_KITS);

      if (kits === target && fee.quantity === target) { lastFeeQty = target; return; }

      return normalize(cart, target, event);
    }).catch(function () {});
  }

  // ---- Reconcile the cart to a correct state -----------------------------
  // Resolves true if it actually wrote to the cart, false if it was already
  // correct. The checkout gate uses that to decide whether the page's own
  // cart form is now stale.
  function ensureFee() {
    return getCart().then(function (cart) {
      var kits = kitCount(cart);
      var fee  = feeLine(cart);
      lastFeeQty = fee ? fee.quantity : null;

      if (kits === 0) {
        if (!fee) return false;
        lastFeeQty = 0;
        return changeByKey(fee.key, 0).then(function () { return true; });
      }

      // NOTE: unlike reconcile(), a missing fee here always means ADD, never
      // "the customer deleted it, so delete the kit". This runs on page load,
      // on bfcache restore and at the checkout gate, where a cart that drifted
      // out of sync must be repaired — deleting kits there would destroy an
      // order over a transient miss.
      var target  = Math.min(kits, MAX_KITS);
      var clamped = kits > target;

      // Over the cap: trim the kit lines first, then fit the fee to the result.
      var step = clamped
        ? updateByKeys(planKitQuantities(cart, target)).then(getCart)
        : Promise.resolve(cart);

      return step.then(function (fresh) {
        var f = feeLine(fresh);
        lastFeeQty = target;

        if (f && !feeHasPlan(f)) {
          return changeByKey(f.key, 0)
            .then(function () { return addFee(target); })
            .then(function () { return true; });
        }
        if (!f) {
          return addFee(target).then(function () { return true; });
        }
        if (f.quantity !== target) {
          return changeByKey(f.key, target).then(function () { return true; });
        }
        return clamped;
      });
    });
  }

  // ---- Load-time reconcile (also fixes any drift) ------------------------
  // Matches /cart, /cart/, and locale-prefixed variants like /en-ca/cart.
  var RELOAD_FLAG = 'maf-repainted';

  function onCartPage() {
    return /(^|\/)cart\/?$/.test(window.location.pathname);
  }

  // ensureFee() writes to the cart AFTER the server already rendered the page,
  // so on /cart the fee is in the data but absent from the DOM. This is the
  // "Buy now -> /cart with no fee" symptom: the line exists, it just isn't
  // drawn. Force the theme to redraw.
  function repaintCartPage() {
    return getCart().then(function (cart) {
      var el  = cartItemsEl();
      var fee = feeLine(cart);

      if (el && fee && typeof el.updateQuantity === 'function') {
        try {
          hold();
          // No-op quantity write — the cart is unchanged, the theme re-renders.
          el.updateQuantity(lineNumberOf(cart, fee), fee.quantity, null, 'maf', FEE_VARIANT_ID);
          return;
        } catch (err) {
          // Some Craft/Dawn forks dereference the event arg. Fall through.
        }
      }

      // No renderer to drive (empty-cart template, drawer-only theme, or the
      // call above threw). One reload, guarded so it can never loop.
      if (onCartPage()) {
        try {
          if (sessionStorage.getItem(RELOAD_FLAG)) return;
          sessionStorage.setItem(RELOAD_FLAG, '1');
        } catch (err) { return; } // private mode: skip rather than risk a loop
        window.location.reload();
      }
    });
  }

  // Prints exactly what the matcher sees. Run window.__maf.report() in the
  // console with the kit in the cart.
  function report() {
    return getCart().then(function (cart) {
      var rows = cart.items.map(function (i) {
        var plan = i.selling_plan_allocation && i.selling_plan_allocation.selling_plan;
        return {
          variant_id: i.variant_id,
          product_id: i.product_id,
          title:      i.product_title,
          variant:    i.variant_title,
          qty:        i.quantity,
          selling_plan: plan ? plan.id : null,
          isKit:      isKitVariant(i.variant_id),
          isFee:      i.variant_id === FEE_VARIANT_ID
        };
      });
      if (window.console && console.table) console.table(rows); else log(rows);
      log('kitCount =', kitCount(cart), '| looking for', KIT_VARIANT_IDS, '| fee', FEE_VARIANT_ID);
      return rows;
    });
  }

  function syncOnLoad() {
    log('syncOnLoad on', window.location.pathname);
    ensureFee().then(function (changed) {
      log('ensureFee changed =', changed);
      if (!changed) {
        // Cart was already correct — clear the guard so a future arrival on
        // /cart that DOES need a repaint is still allowed one reload.
        try { sessionStorage.removeItem(RELOAD_FLAG); } catch (err) {}
        lockQuantities(); // lastCart is now populated for the index fallback
        return;
      }
      if (onCartPage() || cartItemsEl()) return repaintCartPage();
    }).then(lockQuantities)
      .catch(function (err) { warn('syncOnLoad failed:', err && err.message); });
  }

  // ---- Checkout gate -----------------------------------------------------
  // Shopify's checkout is not a themed page, so nothing of ours runs there.
  // Any Checkout click has to reconcile BEFORE the browser leaves, or the
  // customer checks out without the fee — the exact symptom where going to
  // /cart first "fixes" it (that page load ran syncOnLoad).
  var CHECKOUT_SEL = '[name="checkout"], a[href="/checkout"], a[href$="/checkout"], a[href*="/checkout?"]';
  var GATE_TIMEOUT = 4000; // never hold a customer hostage to a hung request
  var gateBusy = false;

  function withTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var settled = false;
      var timer = setTimeout(function () {
        if (!settled) { settled = true; resolve(false); }
      }, ms);
      promise.then(
        function (v) { if (!settled) { settled = true; clearTimeout(timer); resolve(v); } },
        function ()  { if (!settled) { settled = true; clearTimeout(timer); resolve(false); } }
      );
    });
  }

  function armCheckoutGate() {
    document.addEventListener('click', function (e) {
      if (gateBusy) return; // our own replayed click — let it through
      var btn = e.target && e.target.closest ? e.target.closest(CHECKOUT_SEL) : null;
      if (!btn || btn.disabled) return;

      e.preventDefault();
      e.stopPropagation();
      gateBusy = true;
      if (btn.setAttribute) btn.setAttribute('aria-busy', 'true');

      withTimeout(ensureFee(), GATE_TIMEOUT).then(function (changed) {
        if (changed) {
          // We just added or resized a line, so the page's cart form no
          // longer matches the cart. Dawn posts positional updates[] inputs;
          // replaying that submit would clobber the fee we just added.
          // Go straight to checkout instead — Dawn saves the cart note on
          // its own via /cart/update.js, so nothing is lost.
          window.location.href = '/checkout';
          return;
        }
        if (btn.removeAttribute) btn.removeAttribute('aria-busy');
        btn.click(); // cart was already correct — let the theme do its thing
        setTimeout(function () { gateBusy = false; }, 2000);
      });
    }, true);
  }

  // ---- Dynamic checkout buttons ------------------------------------------
  // "Buy it now" / Shop Pay skips the cart entirely, so the fee can never be
  // attached. Hide it on kit variants; re-evaluated on every variant change.
  function hideDynamicCheckout() {
    document.querySelectorAll('product-form').forEach(function (pf) {
      var idInput = pf.querySelector('[name="id"]');
      var vid     = idInput ? parseInt(idInput.value, 10) : NaN;
      var dyn     = pf.parentElement && pf.parentElement.querySelector('.shopify-payment-button');
      if (!dyn) return;
      dyn.style.display = isKitVariant(vid) ? 'none' : '';
    });
  }

  // ---- Lazy re-hook ------------------------------------------------------
  // Quick-add modals, drawers and section re-renders create <product-form>
  // elements long after DOMContentLoaded. Rather than a global
  // MutationObserver (which fought the quantity steppers), re-scan on the
  // first pointer/keyboard interaction — that always precedes a submit, and
  // both hook functions are idempotent, so repeat scans cost a querySelector.
  function armLazyHook() {
    ['pointerdown', 'focusin', 'change'].forEach(function (evt) {
      document.addEventListener(evt, function () {
        hookProductForms();
        hookCartItems();
        hideDynamicCheckout(); // variant pickers swap the id without a reload
        lockQuantities();      // ...and re-render the steppers we just hid
      }, true);
    });
  }

  function init() {
    // Console handle: proves the script loaded, and exposes the internals for
    // manual testing. window.__maf.report() / .ensureFee() / .addFee(1)
    window.__maf = {
      KIT_VARIANT_IDS: KIT_VARIANT_IDS,
      FEE_VARIANT_ID: FEE_VARIANT_ID,
      FEE_SELLING_PLAN_ID: FEE_SELLING_PLAN_ID,
      MAX_KITS: MAX_KITS,
      report: report,
      getCart: getCart,
      ensureFee: ensureFee,
      addFee: addFee,
      lockQuantities: lockQuantities,
      kitCount: function () { return getCart().then(kitCount); }
    };
    log('loaded; watching', KIT_VARIANT_IDS, '| cap', MAX_KITS);

    injectStyle(); // before first paint, so the :has() rule beats the stepper
    hookProductForms();
    hookCartItems();
    hideDynamicCheckout();
    lockQuantities();
    armLazyHook();
    armCheckoutGate();
    syncOnLoad();

    // Back-button restores from bfcache without re-running init, so a cart
    // that drifted while the customer was away would render stale.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) syncOnLoad();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
